import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, DollarSign, Plus, X, Package } from "lucide-react";

const FIELDS = [
  { k: "first_consult", label: "Valor da primeira consulta" },
  { k: "followup", label: "Valor da sessão de acompanhamento" },
  { k: "online", label: "Valor da consulta online" },
  { k: "other", label: "Valor de outros serviços" },
];

export default function Values() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pkg, setPkg] = useState({ name: "", sessions: "", price: "" });

  useEffect(() => {
    if (user) { setForm({ ...user.values }); setPackages(user.values?.packages || []); }
  }, [user]);
  if (!form) return null;

  const addPackage = () => {
    if (!pkg.name || !pkg.price) { toast.error("Informe nome e valor do pacote"); return; }
    setPackages([...packages, { name: pkg.name, sessions: parseInt(pkg.sessions) || 0, price: parseFloat(pkg.price) || 0 }]);
    setPkg({ name: "", sessions: "", price: "" });
  };
  const removePackage = (i) => setPackages(packages.filter((_, j) => j !== i));

  const save = async () => {
    const payload = { packages };
    FIELDS.forEach((f) => { payload[f.k] = form[f.k] ? parseFloat(form[f.k]) : 0; });
    await api.put("/therapist/values", payload); await refresh(); toast.success("Valores atualizados");
  };

  return (
    <div>
      <PageHeader title="Meus Valores" subtitle="Você define os valores dos seus atendimentos. O administrador não interfere."
        action={<Button onClick={save} className="rounded-full" data-testid="save-values-btn"><Save className="w-4 h-4 mr-1" />Salvar</Button>} />
      <Card className="p-6 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.k}>
              <Label>{f.label}</Label>
              <div className="relative mt-1">
                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input type="number" value={form[f.k] ?? ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`value-${f.k}`} className="pl-9" placeholder="0,00" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 max-w-2xl mt-4">
        <div className="flex items-center gap-2 mb-4"><Package className="w-5 h-5 text-primary" /><h3 className="font-heading font-medium">Pacotes</h3></div>
        <div className="space-y-2 mb-4">
          {packages.map((p, i) => (
            <div key={i} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`pkg-${i}`}>
              <div className="flex-1"><div className="font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.sessions ? `${p.sessions} sessões · ` : ""}R$ {Number(p.price).toFixed(2)}</div></div>
              <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removePackage(i)}><X className="w-4 h-4" /></Button>
            </div>
          ))}
          {packages.length === 0 && <p className="text-sm text-muted-foreground">Nenhum pacote cadastrado.</p>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
          <div className="sm:col-span-2"><Label>Nome do pacote</Label><Input value={pkg.name} onChange={(e) => setPkg({ ...pkg, name: e.target.value })} data-testid="pkg-name" className="mt-1" placeholder="Pacote 4 sessões" /></div>
          <div><Label>Sessões</Label><Input type="number" value={pkg.sessions} onChange={(e) => setPkg({ ...pkg, sessions: e.target.value })} className="mt-1" /></div>
          <div><Label>Valor (R$)</Label><Input type="number" value={pkg.price} onChange={(e) => setPkg({ ...pkg, price: e.target.value })} data-testid="pkg-price" className="mt-1" /></div>
        </div>
        <Button variant="outline" className="rounded-full mt-3" onClick={addPackage} data-testid="add-pkg-btn"><Plus className="w-4 h-4 mr-1" />Adicionar pacote</Button>
        <p className="text-sm text-muted-foreground mt-4">Os clientes visualizarão estes valores e pacotes ao agendar uma consulta com você.</p>
      </Card>
    </div>
  );
}
